import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import {
  programmingSkills,
  frontendSkills,
  backendSkills,
} from "../utils/constants";
import { FaCode, FaPaintBrush, FaServer } from "react-icons/fa";
import {
  SiC,
  SiPython,
  SiJavascript,
  SiHtml5,
  SiCss3,
  SiReact,
  SiBootstrap,
  SiTailwindcss,
  SiFigma,
  SiCanva,
  SiNodedotjs,
  SiMongodb,
  SiMysql,
  SiPostman,
  SiGit,
  SiGithub,
} from "react-icons/si";
import { DiJava } from "react-icons/di";
import { SiAmazonwebservices } from "react-icons/si";
const Skills = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Icon mapping
  const iconMap = {
    SiC,
    SiPython,
    DiJava,
    SiJavascript,
    SiHtml5,
    SiCss3,
    SiReact,
    SiBootstrap,
    SiTailwindcss,
    SiFigma,
    SiCanva,
    SiNodedotjs,
    SiMongodb,
    SiMysql,
    SiPostman,
    SiAmazonwebservices,
    SiGit,
    SiGithub,
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.7, ease: [0.4, 0, 0.2, 1] },
    },
  };

  const skillCategories = [
    {
      icon: <FaCode className="text-accent-primary" />,
      title: "Programming",
      subtitle: "Core Languages",
      skills: programmingSkills,
      gradient: "from-accent-primary to-accent-secondary",
    },
    {
      icon: <FaPaintBrush className="text-accent-primary" />,
      title: "Frontend",
      subtitle: "UI/UX Development",
      skills: frontendSkills,
      gradient: "from-accent-secondary to-accent-tertiary",
    },
    {
      icon: <FaServer className="text-accent-primary" />,
      title: "Backend & Tools",
      subtitle: "Server & Database",
      skills: backendSkills,
      gradient: "from-accent-tertiary to-accent-primary",
    },
  ];

  return (
    <section id="skills" className="section-padding relative overflow-hidden">
      {/* Animated background elements */}
      <div className="absolute top-1/4 left-0 w-72 h-72 bg-accent-secondary opacity-5 rounded-full blur-3xl animate-float-3" />
      <div className="absolute bottom-1/4 right-0 w-80 h-80 bg-accent-tertiary opacity-5 rounded-full blur-3xl animate-float-2" />

      <motion.div
        ref={ref}
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
        className="max-w-6xl mx-auto relative z-10"
      >
        <motion.div variants={itemVariants} className="text-center mb-16">
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl font-black gradient-text mb-6">
            Technical Expertise
          </h2>
          <p className="text-lg sm:text-xl text-text-secondary font-light max-w-2xl mx-auto">
            Technologies and tools I work with
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10"
        >
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={categoryIndex}
              variants={itemVariants}
              whileHover={{
                y: -15,
                scale: 1.02,
              }}
              className="glass-effect rounded-3xl p-10 transition-smooth hover:shadow-[0_25px_60px_rgba(76,201,240,0.3)] group relative overflow-hidden"
            >
              {/* Gradient overlay on hover */}
              <motion.div
                className={`absolute inset-0 bg-gradient-to-br ${category.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-500`}
              />

              <div className="text-center mb-8 relative z-10">
                <motion.div
                  className="text-5xl mb-4 flex justify-center"
                  whileHover={{
                    scale: 1.2,
                    rotate: 360,
                  }}
                  transition={{
                    type: "spring",
                    stiffness: 200,
                    damping: 10,
                  }}
                >
                  {category.icon}
                </motion.div>
                <h3 className="text-2xl font-bold text-accent-primary mb-2">
                  {category.title}
                </h3>
                <p className="text-sm text-text-muted uppercase tracking-wider">
                  {category.subtitle}
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 relative z-10">
                {category.skills.map((skill, skillIndex) => {
                  const IconComponent = iconMap[skill.icon];
                  return (
                    <motion.div
                      key={skillIndex}
                      whileHover={{
                        y: -8,
                        scale: 1.1,
                        borderColor: "var(--accent-primary)",
                        backgroundColor: "rgba(76, 201, 240, 0.1)",
                      }}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={
                        inView
                          ? {
                              opacity: 1,
                              scale: 1,
                            }
                          : {
                              opacity: 0,
                              scale: 0.8,
                            }
                      }
                      transition={{
                        delay: categoryIndex * 0.2 + skillIndex * 0.05,
                        type: "spring",
                        stiffness: 200,
                        damping: 15,
                      }}
                      className="flex flex-col items-center justify-center gap-3 p-4 bg-bg-tertiary rounded-xl border border-[rgba(76,201,240,0.05)] transition-smooth cursor-pointer min-h-[90px] group/skill relative overflow-hidden"
                    >
                      {/* Glow effect on hover */}
                      <motion.div className="absolute inset-0 bg-gradient-to-br from-accent-primary to-accent-secondary opacity-0 group-hover/skill:opacity-20 blur-xl transition-opacity duration-300" />

                      {IconComponent && (
                        <motion.div
                          whileHover={{ rotate: 360 }}
                          transition={{ duration: 0.6 }}
                        >
                          <IconComponent className="text-2xl text-accent-primary transition-all duration-300 relative z-10" />
                        </motion.div>
                      )}
                      <span className="text-xs sm:text-sm text-text-secondary font-semibold text-center transition-colors duration-300 group-hover/skill:text-accent-primary relative z-10">
                        {skill.name}
                      </span>
                    </motion.div>
                  );
                })}
              </div>

              {/* Decorative corner elements */}
              <div className="absolute top-3 right-3 w-4 h-4 border-t-2 border-r-2 border-accent-primary opacity-20 group-hover:opacity-100 transition-opacity" />
              <div className="absolute bottom-3 left-3 w-4 h-4 border-b-2 border-l-2 border-accent-primary opacity-20 group-hover:opacity-100 transition-opacity" />
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default Skills;
